import "./Erreur.scss";

import { useEffect } from "react";
import { GabaritNavigationUtil } from "../../../utils/gabarit-navigation/GabaritNavigationUtil";
import { LangueUtils } from "@oel/js-i18n";
import { fr } from "./i18n/Erreur.i18n.fr";
import { en } from "./i18n/Erreur.i18n.en";
import { useSearchParams } from "react-router-dom";
import { ConfigurationService } from "@oel/js-configuration";
import { IdPageEnum } from "../../../utils/gabarit-navigation/IdPageEnum";

type ErreurProps = {
    idPage: IdPageEnum;
}

export function Erreur({ idPage }: Readonly<ErreurProps>) {
    const MESSAGE_GENERIC = "GENERIC";
    const PAAS_SPA_URL_PROD = "https://accesdc.mouv.desjardins.com";
    const OEL_PAAS_SPA_URL = "oel-paas-spa-url";

    const [searchParams] = useSearchParams();
    const libelles = new LangueUtils(fr, en).obtenirLibelles();
    const appMobile = ConfigurationService.getConfig("oel-is-app") === "true";
    // URL de PROD par défaut afin d'éviter des erreurs si la configuration n'est pas définie.
    const paasSpaUrl = ConfigurationService.getConfig(OEL_PAAS_SPA_URL) || PAAS_SPA_URL_PROD;

    const titre = determinerTitreErreur();
    const messageErreur = determinerCodeErreur();
    const estMessageAvecHtml = estMessageAvecFormatageHtml();
    const dsdAlertType = determinerDsdAlertType();

    useEffect(() => {
        document.title = titreOnglet();
        GabaritNavigationUtil.afficherGabarit(idPage);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPage]);

    function determinerTitreErreur(): string {
        const codeErreur = obtenirCodeErreur();
        if (estCodeErreurValide(codeErreur) && libelles[codeErreur]?.titre) {
            return libelles[codeErreur].titre;
        }
        return libelles.titre;
    }

    function determinerCodeErreur(): string {
        const codeErreur = obtenirCodeErreur();
        if (estCodeErreurValide(codeErreur)) {
            return traiterTexte(codeErreur) || libelles[codeErreur] || libelles[MESSAGE_GENERIC];
        }
        return libelles[MESSAGE_GENERIC];
    }

    function traiterTexte(codeErreur): string {
        // Remplacer le placeholder "oel-paas-spa-url" par l'url de la configuration.
        if (libelles[codeErreur]?.texte?.includes(OEL_PAAS_SPA_URL)) {
            return libelles[codeErreur].texte.replace(OEL_PAAS_SPA_URL, paasSpaUrl);
        }

        return libelles[codeErreur]?.texte;
    }

    function estMessageAvecFormatageHtml(): boolean {
        const codeErreur = obtenirCodeErreur();
        if (estCodeErreurValide(codeErreur) && libelles[codeErreur]?.estFormatageHtml) {
            return libelles[codeErreur].estFormatageHtml;
        }
        return false;
    }

    function titreOnglet(): string {
        return !appMobile ? libelles.titreOnglet : libelles.titreOnglet.split(" |")[0];
    }

    function determinerDsdAlertType(): string {
        const codeErreur = obtenirCodeErreur();
        if (estCodeErreurValide(codeErreur) && libelles[codeErreur]?.dsdAlertType) {
            return libelles[codeErreur].dsdAlertType;
        }

        return "inactive";
    }

    function obtenirCodeErreur(): string {
        return searchParams.get("codeErreur") || "";
    }

    function estCodeErreurValide(code: string): boolean {
        const codeErreurValide = new RegExp("^[A-Za-z]{0,3}-?\\d{0,6}$");
        return codeErreurValide.test(code);
    }

    return (
        <div className={`${appMobile ? "erreur-mobile" : "erreur-desktop"}`}>
            <h1 className="dsd-sr-only" id="titre-page-pour-retour-contenu-principal">
                {titre}
            </h1>
            <dsd-alert type={`${dsdAlertType}`}>
                {estMessageAvecHtml ? <p dangerouslySetInnerHTML={{ __html: messageErreur }} /> : <p>{messageErreur}</p>}
            </dsd-alert>
        </div>
    );
}
